/**
 * Dashboard Home tab: current booking, explore, featured shops, map (BooksyAppExpo-style).
 */
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaCalendar,
  FaClock,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaUser,
} from 'react-icons/fa';
import { http } from '../../lib/http';
import { createImageHandler } from '../../utils/imageHandler';
import DateIcon from '../DateIcon';
import { useI18n } from '../../contexts/I18n';
import { Card, Error } from './Card';
import AuthRequiredPanel, { isUnauthorizedError } from './AuthRequiredPanel';
import { cleanText, safe, formatDate } from './utils';

const HomeTab = memo(function HomeTab({
  booking,
  busyBooking,
  errBooking,
  onCancel,
  onReview,
  forceShowDetails = false,
}) {
  const navigate = useNavigate();
  const { t, lang } = useI18n();

  const [images, setImages] = useState([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);
  const [shops, setShops] = useState([]);
  const [serviceCounts, setServiceCounts] = useState({});
  const [shopsLoading, setShopsLoading] = useState(true);
  const [userLocationCoords, setUserLocationCoords] = useState(null);
  const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
  const shopNameRef = useRef(null);

  const normalizeImageUrl = useCallback((u) => {
    if (!u) return null;
    const raw = typeof u === 'string' ? u : (u?.url || u?.src || u?.path || '');
    if (!raw) return null;
    if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('data:') || raw.startsWith('blob:'))
      return raw;
    let s = String(raw).replace(/\\/g, '/').replace(/^\.?\/*/, '');
    if (s.startsWith('uploads/')) return `${window.location.origin}/${s}`;
    if (s.startsWith('/uploads/')) return `${window.location.origin}${s}`;
    if (s.startsWith('/')) return `${window.location.origin}${s}`;
    return `${window.location.origin}/${s}`;
  }, []);

  const listToAbs = useCallback(
    (arr = []) =>
      (Array.isArray(arr) ? arr : []).map(normalizeImageUrl).filter(Boolean),
    [normalizeImageUrl]
  );

  const bookingName = booking?.shopName || '';
  const bookingProvider = booking?.provider || '';
  const bookingId = booking?._id || booking?.id || '';

  useEffect(() => {
    setShowAppointmentDetails(!!forceShowDetails);
  }, [bookingId, forceShowDetails]);

  useLayoutEffect(() => {
    const el = shopNameRef.current;
    if (!el) return undefined;

    const fitShopName = () => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      el.style.transform = '';
      el.style.transformOrigin = '';
      if (!isMobile) {
        el.style.fontSize = '';
        return;
      }

      const available = el.parentElement?.clientWidth || el.clientWidth;
      if (!available) return;

      // Measure at full size, then pick a font size that fits with a small safety margin
      let size = 15.2;
      el.style.fontSize = `${size}px`;
      el.style.maxWidth = 'none';
      el.style.width = 'max-content';
      el.style.overflow = 'visible';
      let needed = el.scrollWidth;
      el.style.width = '';
      el.style.maxWidth = '100%';

      if (needed > available) {
        size = Math.max(11.5, (size * (available / needed)) * 0.99);
        el.style.fontSize = `${size}px`;
      }

      // Subpixel / bold glyph safety: keep shrinking until fully visible
      let guard = 40;
      while (guard-- > 0 && size > 11.5 && el.scrollWidth > available) {
        size -= 0.2;
        el.style.fontSize = `${size}px`;
      }

      // Last resort: scale so every character stays visible
      if (el.scrollWidth > available) {
        const scale = Math.max(0.82, available / el.scrollWidth);
        el.style.transformOrigin = lang === 'ar' ? 'right center' : 'left center';
        el.style.transform = `scale(${scale})`;
      }
      el.style.overflow = '';
    };

    const runFit = () => {
      fitShopName();
      requestAnimationFrame(fitShopName);
    };

    runFit();
    const fontsReady = document.fonts?.ready?.then?.(runFit);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(runFit) : null;
    const parent = el.parentElement;
    const row = parent?.parentElement;
    if (ro && parent) ro.observe(parent);
    if (ro && row) ro.observe(row);
    window.addEventListener('resize', runFit);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', runFit);
      fontsReady?.catch?.(() => {});
    };
  }, [bookingName, lang]);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        if (!bookingName && !bookingProvider) {
          setImages(['/images/shop-placeholder.png']);
          setImagesLoaded(true);
          setHeroIdx(0);
          return;
        }
        const listDoc = await http.getJson('/businesses?limit=200&page=1').catch(() => ({ data: [] }));
        const data = Array.isArray(listDoc?.data) ? listDoc.data : [];
        const targetName = (bookingName || '').trim().toLowerCase();
        const found = data.find(
          (s) =>
            (s.name || '').trim().toLowerCase() === targetName ||
            (bookingProvider && String(s._id) === String(bookingProvider))
        );
        if (!found) {
          if (!ac.signal.aborted) {
            setImages(['/images/shop-placeholder.png']);
            setImagesLoaded(true);
            setHeroIdx(0);
          }
          return;
        }
        let detail = null;
        const slugOrId = found.slug || found._id;
        if (slugOrId) {
          try {
            const resDetail = await http.fetch(`/api/businesses/${slugOrId}`, {
              cache: 'no-store',
              signal: ac.signal,
            });
            if (resDetail.ok) {
              const d = await resDetail.json();
              detail = d?.business ?? d ?? null;
            }
          } catch (err) {}
        }
        const cover = normalizeImageUrl(
          (detail?.imageUrl && detail.imageUrl) || (found?.imageUrl && found.imageUrl)
        );
        const gallery = [
          ...(Array.isArray(detail?.gallery) ? detail.gallery : []),
          ...(Array.isArray(found?.gallery) ? found.gallery : []),
        ];
        const srcs = [cover, ...listToAbs(gallery)].filter(Boolean);
        const uniq = Array.from(new Set(srcs));
        if (!ac.signal.aborted) {
          const finalImages = uniq.length ? uniq : ['/images/shop-placeholder.png'];
          if (finalImages[0] && finalImages[0] !== '/images/shop-placeholder.png') {
            const img = new Image();
            img.onload = () => {
              if (!ac.signal.aborted) {
                setImages(finalImages);
                setImagesLoaded(true);
                setHeroIdx(0);
              }
            };
            img.onerror = () => {
              if (!ac.signal.aborted) {
                setImages(['/images/shop-placeholder.png']);
                setImagesLoaded(true);
                setHeroIdx(0);
              }
            };
            img.src = finalImages[0];
          } else {
            setImages(finalImages);
            setImagesLoaded(true);
            setHeroIdx(0);
          }
        }
      } catch (err) {
        if (!ac.signal.aborted) {
          setImages(['/images/shop-placeholder.png']);
          setImagesLoaded(true);
          setHeroIdx(0);
        }
      }
    })();
    return () => ac.abort();
  }, [bookingName, bookingProvider, listToAbs, normalizeImageUrl]);

  const serviceCategories = [
    { title: 'Barber', titleAr: 'حلاق', image: '/images/barbershop.jpg', slug: 'barber' },
    { title: 'Hair & Nail', titleAr: 'صالون شعر وأظافر', image: '/images/hair.jpg', slug: 'hair-&-nail' },
    { title: 'Laser Clinics', titleAr: 'عيادات الليزر', image: '/images/hair1.jpg', slug: 'laser-clinics' },
    { title: 'Makeup', titleAr: 'مكياج', image: '/images/makeup.jpg', slug: 'makeup' },
    { title: 'Massage', titleAr: 'مساج', image: '/images/massage.jpg', slug: 'massage' },
    { title: 'Pet Services', titleAr: 'خدمات الحيوانات الأليفة', image: '/images/pet.png', slug: 'pet-services' },
  ];

  useEffect(() => {
    const fetchShops = async () => {
      try {
        setShopsLoading(true);
        const { data } = await http.getJson('/businesses?limit=100&page=1');
        const shopsData = Array.isArray(data) ? data : [];
        setShops(shopsData);
        const counts = {};
        shopsData.forEach((shop) => {
          const category = shop.serviceCategory;
          if (category) counts[category] = (counts[category] || 0) + 1;
        });
        setServiceCounts(counts);
      } catch (err) {
        setShops([]);
      } finally {
        setShopsLoading(false);
      }
    };
    fetchShops();
  }, []);

  const featuredShops = useMemo(
    () =>
      shops
        .slice()
        .sort((a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0))
        .slice(0, 7),
    [shops]
  );

  const [isMobileView, setIsMobileView] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 960px)').matches
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 960px)');
    const onChange = () => setIsMobileView(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const topShopsLimit = isMobileView ? 4 : 7;

  const getShopCoordinates = useCallback((shop) => {
    if (shop.latitude && shop.longitude)
      return {
        latitude: parseFloat(shop.latitude),
        longitude: parseFloat(shop.longitude),
      };
    if (
      shop.location?.coordinates &&
      Array.isArray(shop.location.coordinates) &&
      shop.location.coordinates.length >= 2
    )
      return {
        latitude: parseFloat(shop.location.coordinates[1]),
        longitude: parseFloat(shop.location.coordinates[0]),
      };
    const cityCoordinates = {
      Cairo: { latitude: 30.0444, longitude: 31.2357 },
      'New Cairo': { latitude: 30.0131, longitude: 31.4046 },
      Heliopolis: { latitude: 30.0886, longitude: 31.3197 },
      'Masr El Gedida': { latitude: 30.0886, longitude: 31.3197 },
      'October City': { latitude: 29.9697, longitude: 30.9564 },
      Alexandria: { latitude: 31.2001, longitude: 29.9187 },
      Giza: { latitude: 30.0131, longitude: 31.2089 },
    };
    const town = (shop.town || shop.city || '').trim();
    if (cityCoordinates[town]) return cityCoordinates[town];
    const found = Object.keys(cityCoordinates).find(
      (key) => key.toLowerCase() === town.toLowerCase()
    );
    return found ? cityCoordinates[found] : cityCoordinates['Cairo'];
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          setUserLocationCoords({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        () => {}
      );
    }
  }, []);

  const generateMapHTML = useMemo(() => {
    const shopsWithCoords = shops
      .filter((shop) => shop && (shop.town || shop.city || shop.name))
      .map((shop) => {
        const coords = getShopCoordinates(shop);
        return {
          id: shop._id || shop.id,
          name: shop.name || 'Shop',
          lat: coords.latitude,
          lng: coords.longitude,
          image: shop.imageUrl || shop.image || '',
          rating: shop.rating || 0,
          town: shop.town || shop.city || '',
        };
      })
      .filter((s) => s.lat && s.lng && !isNaN(s.lat) && !isNaN(s.lng));
    let centerLat = 30.0444;
    let centerLng = 31.2357;
    let zoom = 13;
    if (userLocationCoords) {
      centerLat = userLocationCoords.latitude;
      centerLng = userLocationCoords.longitude;
    } else if (shopsWithCoords.length > 0) {
      const lats = shopsWithCoords.map((s) => s.lat);
      const lngs = shopsWithCoords.map((s) => s.lng);
      centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
      zoom = 12;
    }

    const previewShops = shopsWithCoords.slice(0, 28);

    return `
<!DOCTYPE html>
<html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body, html { width: 100%; height: 100%; overflow: hidden; background: #F3EDE3; }
    #map { width: 100%; height: 100%; }
    .leaflet-control-attribution { display: none !important; }
    .shop-pin {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      overflow: hidden;
      border: none;
      box-shadow: 0 3px 8px rgba(24, 68, 42, 0.35);
      background: #18442A;
    }
    .shop-pin img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    .user-dot {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #18442A;
      border: 2px solid #ffffff;
      box-shadow: 0 0 0 4px rgba(24, 68, 42, 0.28), 0 2px 6px rgba(0,0,0,0.22);
    }
  </style>
</head>
<body>
  <div id="map"></div>
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
  <script>
    const map = L.map('map', {
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false
    }).setView([${centerLat}, ${centerLng}], ${zoom});

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19
    }).addTo(map);

    const shops = ${JSON.stringify(previewShops)};
    const userLocation = ${userLocationCoords ? JSON.stringify({ lat: userLocationCoords.latitude, lng: userLocationCoords.longitude }) : 'null'};

    if (userLocation) {
      L.marker([userLocation.lat, userLocation.lng], {
        icon: L.divIcon({
          className: '',
          html: '<div class="user-dot"></div>',
          iconSize: [14, 14],
          iconAnchor: [7, 7]
        })
      }).addTo(map);
    }

    shops.forEach((shop) => {
      if (!shop.lat || !shop.lng || isNaN(shop.lat) || isNaN(shop.lng)) return;
      const src = shop.image || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=200';
      L.marker([shop.lat, shop.lng], {
        icon: L.divIcon({
          className: '',
          html: '<div class="shop-pin"><img src="' + src + '" alt="" /></div>',
          iconSize: [28, 28],
          iconAnchor: [14, 14]
        })
      }).addTo(map);
    });
  </script>
</body>
</html>`;
  }, [shops, userLocationCoords, getShopCoordinates]);

  const unauthorized = isUnauthorizedError(errBooking);
  const hasBooking = booking && typeof booking === 'object' && Object.keys(booking).length > 0;

  const renderBookingPanel = () => {
    if (busyBooking) {
      return (
        <div className="dashboard-booking-card dashboard-current-appointment-loading">
          <div className="dashboard-loading-spinner" aria-hidden="true" />
        </div>
      );
    }
    if (unauthorized) {
      return <AuthRequiredPanel hint={t('dashSessionExpiredHint')} />;
    }
    if (errBooking) {
      return (
        <div className="dashboard-auth-required dashboard-auth-required--error">
          <Error>{errBooking}</Error>
          <button
            type="button"
            className="dh-btn dh-btn--primary"
            onClick={() => window.location.reload()}
          >
            {t('dashTryAgain') || 'Try again'}
          </button>
        </div>
      );
    }
    if (hasBooking) {
      return (
        <div className="dashboard-booking-card">
          <div className={`dashboard-booking-top-row ${lang === 'ar' ? 'rtl' : ''}`}>
            <div className="dashboard-booking-title-block">
              <div className={`dashboard-booking-shop-row ${lang === 'ar' ? 'rtl' : ''}`}>
                {imagesLoaded && images[0] && images[0] !== '/images/shop-placeholder.png' ? (
                  <img
                    src={images[0]}
                    alt={booking.shopName || 'Shop'}
                    className="dashboard-booking-shop-image"
                    loading="eager"
                    onError={(e) => { e.target.src = '/images/shop-placeholder.png'; }}
                  />
                ) : (
                  <div className="shop-image-loading-buffer dashboard-booking-shop-image" aria-hidden="true" />
                )}
                <div className="dh-booking-copy">
                  <span ref={shopNameRef} className="dashboard-booking-shop-name">
                    {cleanText(safe(booking.shopName || '—'))}
                  </span>
                  <span className="dh-booking-status">{booking.status}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            className={`dh-booking-meta ${showAppointmentDetails ? 'is-open' : ''} ${lang === 'ar' ? 'rtl' : ''}`}
          >
            <div className="dh-booking-meta__item">
              <FaCalendar aria-hidden="true" />
              <span>
                {formatDate(booking.selectedDate || booking.date || booking.appointmentTime) || '—'}
              </span>
            </div>
            <div className="dh-booking-meta__item">
              <FaClock aria-hidden="true" />
              <span>
                {cleanText(safe(booking.selectedHour || booking.time || booking.appointmentTime || '—'))}
              </span>
            </div>
            <div className="dh-booking-meta__item">
              <FaUser aria-hidden="true" />
              <span>
                {cleanText(
                  safe(
                    booking.staffName ||
                      (typeof booking.staff === 'string' ? booking.staff : booking.staff?.name) ||
                      '—'
                  )
                )}
              </span>
            </div>
          </div>
          <div className="dashboard-booking-stepper-wrapper">
            <div className="dashboard-booking-stepper">
              <div className="dashboard-stepper-track">
                <div className="dashboard-stepper-track-background" />
                <div
                  className="dashboard-stepper-track-progress"
                  style={{
                    width: `${({ pending: 0, accepted: 50, completed: 100, cancelled: 0, declined: 0 }[booking.status] ?? 0)}%`,
                    backgroundColor: ({ pending: '#b45309', accepted: '#18442A', completed: '#047857', cancelled: '#b91c1c', declined: '#b91c1c' }[booking.status] ?? '#b45309'),
                  }}
                />
              </div>
              <div className={`dashboard-stepper-dots-row ${lang === 'ar' ? 'rtl' : ''}`}>
                {[
                  { key: 'pending', label: t('dashStepPending'), Icon: FaClock, color: '#b45309' },
                  { key: 'accepted', label: t('dashStepAccepted'), Icon: FaCheckCircle, color: '#18442A' },
                  { key: 'completed', label: t('dashStepCompleted'), Icon: FaCheckCircle, color: '#047857' },
                ].map((step, index) => {
                  const currentStep = { pending: 0, accepted: 1, completed: 2, cancelled: 0, declined: 0 }[booking.status] ?? 0;
                  const isActive = index <= currentStep;
                  const isCurrent = index === currentStep;
                  const IconComponent = step.Icon;
                  return (
                    <div key={step.key} className="dashboard-stepper-dot-wrapper">
                      <div
                        className={`dashboard-stepper-circle ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}
                        style={{ backgroundColor: isActive ? step.color : '#ffffff', borderColor: step.color }}
                      >
                        <IconComponent style={{ color: isActive ? '#ffffff' : '#64748b' }} />
                      </div>
                      <span className={`dashboard-stepper-label ${isActive ? 'active' : ''}`}>{step.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className={`dh-booking-actions ${lang === 'ar' ? 'rtl' : ''}`}>
            <button
              type="button"
              className="dh-btn dh-btn--details-toggle"
              onClick={() => setShowAppointmentDetails((prev) => !prev)}
            >
              {showAppointmentDetails
                ? (lang === 'ar' ? 'إخفاء التفاصيل' : 'Hide details')
                : (lang === 'ar' ? 'عرض التفاصيل' : 'Show details')}
            </button>
            {booking.status === 'pending' && (
              <button type="button" className="dh-btn dh-btn--danger" onClick={onCancel}>
                {lang === 'ar' ? 'إلغاء الموعد' : 'Cancel Appointment'}
              </button>
            )}
            {booking.status === 'completed' && !booking.reviewed && onReview && (
              <button type="button" className="dh-btn dh-btn--primary" onClick={() => onReview(booking)}>
                {t('dashReview')}
              </button>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="dh-empty-booking">
        <DateIcon />
        <p className="dh-empty-booking__text">{t('dashNoActive') || 'No current booking'}</p>
        <p className="dh-empty-booking__hint">
          {lang === 'ar' ? 'اختر فئة وابدأ الحجز في دقائق' : 'Pick a category and book in minutes'}
        </p>
        <button type="button" className="dh-btn dh-btn--primary" onClick={() => navigate('/explore')}>
          {lang === 'ar' ? 'استكشف فئاتنا' : 'Explore categories'}
        </button>
      </div>
    );
  };

  return (
    <div className={`dashboard-home-root ${lang === 'ar' ? 'rtl' : ''}`}>
      <section className="dashboard-service-categories-hero" aria-labelledby="dh-cat-title">
        <div className="dashboard-categories-intro">
          <h3 id="dh-cat-title" className="dashboard-categories-intro__title">
            {lang === 'ar' ? 'تصفح حسب الفئة' : 'Browse by category'}
          </h3>
        </div>
        <div className={`dashboard-service-categories-scroll ${lang === 'ar' ? 'rtl' : ''}`}>
          {serviceCategories.map((service) => (
            <Link key={service.title} to={`/service/${service.slug}`} className="dashboard-service-chip">
              <div className="dashboard-service-chip-photo">
                <img
                  src={service.image}
                  alt={lang === 'ar' ? (service.titleAr || service.title) : service.title}
                />
              </div>
              <div className="dashboard-service-chip-label-container">
                <div className="dashboard-service-chip-label-wrap">
                  <span className="dashboard-service-chip-label-brush" aria-hidden="true" />
                  <div className="dashboard-service-chip-label">
                    {lang === 'ar' ? (service.titleAr || service.title) : service.title}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <div className="dashboard-home-container dh">
        <div className="dh-board">
          <div className="dh-board__main">
            <section className="dh-hero" aria-label={t('dashCurrentAppt')}>
              <div className={hasBooking || busyBooking ? 'dh-surface dh-surface--hero' : 'dh-hero-empty'}>
                {renderBookingPanel()}
              </div>
            </section>

            <section className="dh-section dh-board__map dh-map-section" aria-labelledby="dh-map-title">
              <div className={`dh-section__head dh-section__head--row ${lang === 'ar' ? 'rtl' : ''}`}>
                <div>
                  <h3 id="dh-map-title" className="dh-section__title">
                    <FaMapMarkerAlt className="dh-section__icon dh-section__icon--amber" aria-hidden="true" />
                    {lang === 'ar' ? 'المتاجر القريبة منك' : 'Shops near you'}
                  </h3>
                  <p className="dh-section__sub">
                    {lang === 'ar' ? 'استكشف الخريطة واختر مكانك' : 'Explore the map & pick your spot'}
                  </p>
                </div>
                <button type="button" className="dh-btn dh-btn--map dh-btn--sm" onClick={() => navigate('/map')}>
                  {lang === 'ar' ? 'افتح الخريطة' : 'Open map'}
                </button>
              </div>
              <div className="dh-map" aria-label={lang === 'ar' ? 'افتح الخريطة' : 'Open Map'}>
                <iframe
                  srcDoc={generateMapHTML}
                  className="dashboard-map-iframe"
                  title="Shops Map"
                  frameBorder="0"
                  scrolling="no"
                />
                <div
                  className="dashboard-map-click-overlay"
                  onClick={() => navigate('/map')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate('/map');
                    }
                  }}
                  aria-label={lang === 'ar' ? 'افتح الخريطة' : 'Open Map'}
                />
              </div>
            </section>
          </div>

          <section className="dh-section dh-shops-section dh-board__shops" aria-labelledby="dh-feat-title">
            <div className="dh-section__head">
              <h3 id="dh-feat-title" className="dh-section__title">
                <span className="dh-yalla">{lang === 'ar' ? 'يلا!' : 'Yalla!'}</span>
                {lang === 'ar' ? 'أفضل المحلات' : 'Top shops'}
              </h3>
              <p className="dh-section__sub">
                {lang === 'ar' ? 'أعلى تقييماً بالقرب منك' : 'Highest rated near you'}
              </p>
            </div>
            {shopsLoading ? (
              <div className="dh-shops-loading" role="status" aria-label="Loading">
                <div className="dashboard-loading-spinner" aria-hidden="true" />
              </div>
            ) : featuredShops.length > 0 ? (
              <div className="dh-shops">
                {featuredShops.slice(0, topShopsLimit).map((shop) => {
                  const shopNameForUrl = shop.name || shop.slug || String(shop._id || shop.id || '');
                  return (
                    <Link
                      key={shop._id || shop.id}
                      to={`/booking/${encodeURIComponent(shopNameForUrl)}`}
                      className="dh-shop"
                    >
                      <img
                        src={shop.imageUrl || '/images/shop-placeholder.png'}
                        alt=""
                        className="dh-shop__img"
                      />
                      <div className="dh-shop__body">
                        <div className="dh-shop__name">{shop.name || shop.slug}</div>
                        <div className="dh-shop__meta">
                          {shop.serviceCategory || '—'}
                          <span className="dh-shop__rating">
                            <span className="dh-shop__star" aria-hidden="true">★</span>
                            {' '}{shop.rating?.toFixed(1) || 'N/A'}
                          </span>
                        </div>
                        <div className="dh-shop__town">
                          <FaMapMarkerAlt aria-hidden="true" />
                          {shop.town || '—'}
                        </div>
                      </div>
                      <span className="dh-shop__book">{lang === 'ar' ? 'احجز' : 'Book'}</span>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <p className="dh-section__sub">
                {lang === 'ar' ? 'لا توجد محلات بعد.' : 'No shops yet.'}
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  );

});

export default HomeTab;
